import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Clock, MessageSquare, HelpCircle, FolderKanban, ListTodo, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, Badge, Thumb } from "./ui";

function Stat({ icon: Icon, label, value, to, testid }) {
  const body = (
    <div className="flex items-center gap-4 border border-black/10 bg-white p-5 transition-shadow hover:shadow-md" data-testid={testid}>
      <Icon className="text-[color:var(--ib-orange)]" size={26} strokeWidth={1.6} />
      <div><p className="font-display text-3xl">{value ?? "–"}</p><p className="text-xs font-semibold uppercase tracking-wider text-black/55">{label}</p></div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setD(r.data));
    api.get("/admin/settings").then((r) => setSettings(r.data));
  }, []);
  return (
    <div>
      <PageTitle title={`Welcome back${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} sub="Here's what's happening with your website." />
      {settings && !settings.launch_confirmed && (
        <div className="mb-6 flex items-start gap-3 border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900" data-testid="launch-checklist-banner">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>Before launch, please confirm your company details, service areas, and imported project details. <Link to="/admin/help" className="font-semibold underline">See the launch checklist</Link>.</div>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Inbox} label="Total inquiries" value={d?.total_leads} to="/admin/leads" testid="stat-total-leads" />
        <Stat icon={Inbox} label="New leads" value={d?.new_leads} to="/admin/leads?status=New" testid="stat-new-leads" />
        <Stat icon={Clock} label="Awaiting response 24h+" value={d?.awaiting_response} to="/admin/leads?status=New" testid="stat-awaiting" />
        <Stat icon={MessageSquare} label="AI conversations" value={d?.ai_conversations} to="/admin/ai?tab=conversations" testid="stat-ai-conversations" />
        <Stat icon={HelpCircle} label="Unanswered AI questions" value={d?.unanswered} to="/admin/ai" testid="stat-unanswered" />
        <Stat icon={FolderKanban} label="Published projects" value={d?.published_projects} to="/admin/projects" testid="stat-projects" />
        <Stat icon={ListTodo} label="Open follow-up tasks" value={d?.open_tasks} to="/admin/ai?tab=tasks" testid="stat-tasks" />
        <Stat icon={Inbox} label="Leads this week" value={d?.leads_this_week} testid="stat-week-leads" />
      </div>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <section className="border border-black/10 bg-white p-5" data-testid="recent-leads">
          <h2 className="font-display text-lg uppercase">Recent inquiries</h2>
          <ul className="mt-3 divide-y">
            {(d?.recent_leads || []).map((l) => (
              <li key={l.id}><Link to={`/admin/leads?id=${l.id}`} className="flex items-center justify-between py-2.5 text-sm hover:text-[color:var(--ib-orange)]"><span><b>{l.name || "Unknown"}</b> · {l.category || "Project"} <span className="text-black/45">via {l.source}</span></span><Badge s={l.status} /></Link></li>
            ))}
            {d && !d.recent_leads.length && <li className="py-3 text-sm text-black/50">No inquiries yet.</li>}
          </ul>
        </section>
        <section className="border border-black/10 bg-white p-5" data-testid="recent-activity">
          <h2 className="font-display text-lg uppercase">Recent website activity</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {(d?.activity || []).map((a) => <li key={a.id} className="flex justify-between gap-3"><span className="truncate">{a.action.replace(/_/g, " ")} <span className="text-black/45">{a.detail}</span></span><span className="shrink-0 text-xs text-black/40">{new Date(a.created_at).toLocaleString()}</span></li>)}
          </ul>
        </section>
        <section className="border border-black/10 bg-white p-5 lg:col-span-2" data-testid="recent-uploads">
          <h2 className="font-display text-lg uppercase">Recent gallery uploads</h2>
          <div className="mt-3 flex gap-2 overflow-x-auto">
            {(d?.recent_media || []).map((m) => <Thumb key={m.id} url={m.url} kind={m.kind} className="h-20 w-28 shrink-0" />)}
            {d && !d.recent_media.length && <p className="text-sm text-black/50">No uploads yet. <Link className="underline" to="/admin/photos">Upload photos</Link></p>}
          </div>
        </section>
      </div>
    </div>
  );
}

function Bar({ label, value, max }) {
  return (
    <div className="text-sm"><div className="flex justify-between"><span className="truncate">{label}</span><span className="font-semibold">{value}</span></div>
      <div className="mt-1 h-2 bg-black/5"><div className="h-2 bg-[color:var(--ib-orange)]" style={{ width: `${max ? (100 * value) / max : 0}%` }} /></div></div>
  );
}

function BarList({ title, rows, testid }) {
  const max = Math.max(1, ...rows.map((r) => r[1]));
  return (
    <section className="border border-black/10 bg-white p-5" data-testid={testid}>
      <h2 className="mb-4 font-display text-lg uppercase">{title}</h2>
      <div className="space-y-3">{rows.length ? rows.map(([l, v]) => <Bar key={l} label={l} value={v} max={max} />) : <p className="text-sm text-black/50">No data yet.</p>}</div>
    </section>
  );
}

export function Analytics() {
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  const [opp, setOpp] = useState(null);
  const { can } = useAuth();
  useEffect(() => {
    api.get("/admin/analytics", { params: { days } }).then((r) => setA(r.data));
    if (can("ai")) api.get("/admin/ai/opportunities", { params: { days } }).then((r) => setOpp(r.data));
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const maxDay = Math.max(1, ...(a?.views_by_day || []).map((d) => d.views));
  return (
    <div>
      <PageTitle title="Analytics" sub="Simple, built-in website stats. Connect Google Analytics 4 in Settings for deeper reporting.">
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="border bg-white px-3 py-2 text-sm" data-testid="analytics-days"><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option></select>
      </PageTitle>
      {a && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {[["Page views", a.page_views], ["Visitors", a.visitors], ["Inquiries", a.total_leads], ["AI conversations", a.ai_conversations], ["AI-generated leads", a.ai_leads], ["Won rate", `${a.conversion_rate}%`]].map(([l, v]) => (
              <div key={l} className="border border-black/10 bg-white p-4"><p className="font-display text-2xl">{v}</p><p className="text-xs uppercase tracking-wider text-black/55">{l}</p></div>
            ))}
          </div>
          <section className="mt-6 border border-black/10 bg-white p-5" data-testid="views-chart">
            <h2 className="mb-4 font-display text-lg uppercase">Page views per day</h2>
            <div className="flex h-40 items-end gap-1">{a.views_by_day.map((d) => <div key={d.day} title={`${d.day}: ${d.views}`} className="flex-1 bg-[color:var(--ib-orange)]/80" style={{ height: `${(100 * d.views) / maxDay}%` }} />)}</div>
          </section>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <BarList title="Most viewed service pages" rows={a.top_services.map((x) => [x.path, x.views])} testid="top-services" />
            <BarList title="Most viewed projects" rows={a.top_projects.map((x) => [x.path, x.views])} testid="top-projects" />
            <BarList title="Lead sources" rows={Object.entries(a.lead_sources)} testid="lead-sources" />
            <BarList title="Lead status / conversion" rows={Object.entries(a.lead_statuses)} testid="lead-statuses" />
            {opp && <BarList title="Popular customer questions" rows={opp.frequent.map((f) => [f.question, f.count])} testid="popular-questions" />}
          </div>
        </>
      )}
    </div>
  );
}
